import React from "react";
import { Link } from "react-router-dom";
import "./HowWeWork.css";
import '@fortawesome/fontawesome-free/css/all.min.css';

const HowWeWork = () => {
  const steps = [
    {
      step: "1",
      title: "Initial Consultation",
      desc: "We listen to you, understand your goals, and answer your questions.",
      details: [
        "Free initial consultation to discuss your situation",
        "Comprehensive assessment of your case",
        "Clear explanation of your legal options",
        "Honest advice about potential outcomes",
        "Transparent discussion about costs and timelines",
        "Opportunity to ask questions and address concerns"
      ]
    },
    {
      step: "2",
      title: "Strategic Planning",
      desc: "We recommend tailored approaches — negotiation, mediation, or court representation.",
      details: [
        "Detailed case analysis and strategy development",
        "Recommendation of the best approach for your situation",
        "Exploration of alternative dispute resolution options",
        "Development of a clear action plan",
        "Setting realistic expectations and timelines",
        "Preparation for each stage of the process"
      ]
    },
    {
      step: "3",
      title: "Clear Communication",
      desc: "You receive regular updates and honest guidance at every step.",
      details: [
        "Regular progress updates via your preferred method",
        "Clear explanations of legal processes and documents",
        "Honest assessment of developments in your case",
        "Prompt responses to your questions and concerns",
        "Accessible communication in plain English",
        "Support and guidance throughout the emotional journey"
      ]
    },
    {
      step: "4",
      title: "Focused Resolution",
      desc: "Our aim is to achieve fair, practical outcomes with minimum stress.",
      details: [
        "Dedicated pursuit of fair outcomes",
        "Minimization of conflict and stress",
        "Efficient case management",
        "Protection of your interests and rights",
        "Sensitive handling of sensitive matters",
        "Finalization with clear documentation"
      ]
    }
  ];

  const values = [
    {
      title: "Transparency",
      icon: "fa-file-lines",
      description: "Clear, honest communication about your case, costs, and what to expect at every stage."
    },
    {
      title: "Empathy",
      icon: "fa-heart",
      description: "Understanding that family law matters are deeply personal and emotionally challenging."
    },
    {
      title: "Expertise",
      icon: "fa-scale-balanced",
      description: "Years of experience and specialized knowledge in all areas of family and matrimonial law."
    },
    {
      title: "Dedication",
      icon: "fa-gavel",
      description: "Committed to achieving the best possible outcome for you and your family."
    }
  ];

  return (
    <div className="page-container">
      {/* Hero Section */}
      <section className="page-hero">
        <div className="page-hero-content">
          <h1>Your Path to Resolution</h1>
          <p className="page-hero-subtitle">
            We believe in transparent legal support. Here's how we work with you to achieve the best outcomes.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="page-content">
        <div className="page-wrapper">
          {/* Intro */}
          <div className="how-intro">
            <h2>Our Four-Step Process</h2>
            <p>
              Every case is unique, but our approach remains consistent: we listen, plan, communicate, and resolve. 
              We're committed to making the legal process as clear and stress-free as possible while fighting for your best interests.
            </p>
          </div>

          {/* Steps Timeline */}
          <div className="steps-timeline">
            {steps.map((item, index) => (
              <div className="step-item" key={index}>
                <div className="step-visual">
                  <div className="step-circle">{item.step}</div>
                  {index < steps.length - 1 && <div className="step-connector"></div>}
                </div>
                <div className="step-content">
                  <h3>{item.title}</h3>
                  <p className="step-desc">{item.desc}</p>
                  <div className="step-details">
                    <h4>What This Includes:</h4>
                    <ul>
                      {item.details.map((detail, idx) => (
                        <li key={idx}>{detail}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Our Values */}
          <div className="values-section">
            <h2>Our Core Values</h2>
            <div className="values-grid">
              {values.map((value, index) => (
                <div className="value-card" key={index}>
                  <div className="value-icon"><i className={`fa-solid ${value.icon}`}></i></div>
                  <h4>{value.title}</h4>
                  <p>{value.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Why Choose Us */}
          <div className="why-choose">
            <h2>Why Choose Our Approach?</h2>
            <div className="why-grid">
              <div className="why-item">
                <h4><i className="fa-solid fa-user-tie"></i> Client-Focused</h4>
                <p>Your needs and goals are at the center of everything we do. We tailor our approach to your unique situation.</p>
              </div>
              <div className="why-item">
                <h4><i className="fa-solid fa-clock"></i> Efficient</h4>
                <p>We work efficiently to resolve matters as quickly as possible while ensuring thoroughness and attention to detail.</p>
              </div>
              <div className="why-item">
                <h4><i className="fa-solid fa-handshake"></i> Collaborative</h4>
                <p>We believe in working collaboratively with you, keeping you informed and involved in decision-making throughout.</p>
              </div>
              <div className="why-item">
                <h4><i className="fa-solid fa-gavel"></i> Strong Advocacy</h4>
                <p>When negotiation isn't enough, we're prepared to advocate strongly for your interests in court.</p>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="page-cta">
            <h2>Ready to Start Your Journey?</h2>
            <p>Book a free consultation to discuss your case and learn how we can help</p>
            <Link to="/contact" className="cta-button">
              Book a Consultation
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HowWeWork;
